import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["as", "children", "className", "truncated", "lineClamp", "tooltipHandle", "layoutId", "style", "variant", "xstyle"],
  _excluded2 = ["as", "children", "className", "truncated", "lineClamp", "tooltipHandle", "layoutId", "style", "variant", "xstyle"];
import { c as _c } from "react-compiler-runtime";
import { forwardRef, useCallback, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useLayoutConstraints } from '../layout/LayoutConstraints';
import { theme } from 'trellis-theming/modern-theme.stylex';
import { DATA_COMPONENT_ATTRIBUTE } from '../../constants/dataAttributes';
import { mergeRefs } from '../../utils/mergeRefs';
import { TooltipRoot, TooltipTrigger, TooltipContent } from '../Tooltip/primitives';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const COMPONENT_NAME = 'Text';
const resetStyles = {
  base: {
    margin: "x15bfwwa",
    padding: "x1i6jvaa",
    textTransform: "x1gdvv3m",
    $$css: true
  }
};
const INLINE_AS_ELEMENTS = new Set(['span', 'strong', 'code']);
const _temp = {
  WebkitBoxOrient: "x1ua5tub",
  display: "x104kibb",
  overflow: "xb3r6kr",
  overflowX: "",
  overflowY: "",
  "$$css": true
};
const truncateStyles = {
  base: {
    minWidth: "xeuugli",
    overflowX: "x101abm8",
    overflowY: "xryxfnj",
    textOverflow: "xlyipyv",
    whiteSpace: "xuxw1ft",
    $$css: true
  },
  inlineBlock: {
    display: "x1rg5ohu",
    maxWidth: "x193iq5w",
    $$css: true
  },
  lineClamp: lineClamp => [_temp, {
    WebkitLineClamp: lineClamp != null ? "x1yhjpo9" : lineClamp,
    lineClamp: lineClamp != null ? "x7qaf8y" : lineClamp,
    $$css: true
  }, {
    "--x-WebkitLineClamp": lineClamp != null ? lineClamp : undefined,
    "--x-lineClamp": lineClamp != null ? lineClamp : undefined
  }]
};
const variantStyles = {
  "hero-1": {
    color: "xnp8pqq",
    fontFamily: "xp73vhd",
    fontSize: "xjfeqmd",
    fontWeight: "x1rxl6sv",
    letterSpacing: "xs39hvp",
    lineHeight: "xpz3xcy",
    $$css: true
  },
  "hero-2": {
    color: "xnp8pqq",
    fontFamily: "x1r4uw6s",
    fontSize: "xhdrvir",
    fontWeight: "x1w7r2g0",
    letterSpacing: "x1xymjmg",
    lineHeight: "x5qluwn",
    $$css: true
  },
  "hero-3": {
    color: "xnp8pqq",
    fontFamily: "x4w206u",
    fontSize: "xfiwwn7",
    fontWeight: "x1c4rjvc",
    letterSpacing: "xvkgdex",
    lineHeight: "x1a291s6",
    $$css: true
  },
  "hero-4": {
    color: "xnp8pqq",
    fontFamily: "x19n9771",
    fontSize: "x1w9zfem",
    fontWeight: "xglybek",
    letterSpacing: "x12w65lw",
    lineHeight: "x1ss3rne",
    $$css: true
  },
  "heading-1": {
    color: "xnp8pqq",
    fontFamily: "xrw7fvx",
    fontSize: "x1n1bt50",
    fontWeight: "x1s45ei9",
    letterSpacing: "x1kshu3q",
    lineHeight: "x1fx0qzd",
    $$css: true
  },
  "heading-2": {
    color: "xnp8pqq",
    fontFamily: "x1p5rf2j",
    fontSize: "x1nf51ny",
    fontWeight: "xr9mvsr",
    letterSpacing: "x6kqsmd",
    lineHeight: "x1chxmzp",
    $$css: true
  },
  "heading-3": {
    color: "xnp8pqq",
    fontFamily: "x1tddzbz",
    fontSize: "x2xjub4",
    fontWeight: "x13wg1ry",
    letterSpacing: "x1y2knqd",
    lineHeight: "x1hqj723",
    $$css: true
  },
  "heading-4": {
    color: "xnp8pqq",
    fontFamily: "x1ckmjs3",
    fontSize: "x9j20o2",
    fontWeight: "x83n6n2",
    letterSpacing: "xfa0l1o",
    lineHeight: "x1ibqt2j",
    $$css: true
  },
  "heading-5": {
    color: "xnp8pqq",
    fontFamily: "x1yxx3iy",
    fontSize: "x1hbkd3u",
    fontWeight: "x1ogaumu",
    letterSpacing: "x7wcvp",
    lineHeight: "x1wdnn3i",
    $$css: true
  },
  "heading-6": {
    color: "xnp8pqq",
    fontFamily: "xm5nqs7",
    fontSize: "xuqkjek",
    fontWeight: "xj0iluz",
    letterSpacing: "x1qobdza",
    lineHeight: "x1m418si",
    $$css: true
  },
  "body-sm": {
    fontFamily: "xg149o6",
    fontSize: "xhdlvez",
    fontWeight: "x1my9jzc",
    letterSpacing: "x1a3cg6y",
    lineHeight: "xgyutik",
    $$css: true
  },
  "body-md": {
    fontFamily: "x6hfyku",
    fontSize: "xe639uq",
    fontWeight: "x1h7urat",
    letterSpacing: "x1ibk7ho",
    lineHeight: "x1x6p1lu",
    $$css: true
  },
  "body-lg": {
    fontFamily: "xfxe8sg",
    fontSize: "x1a7hurv",
    fontWeight: "xfgrtbu",
    letterSpacing: "x1zvb9h",
    lineHeight: "x1urda5o",
    $$css: true
  },
  strong: {
    fontWeight: "x9x4hfr",
    $$css: true
  },
  code: {
    fontFamily: "x1je1o9w",
    fontWeight: "x1h7urat",
    $$css: true
  },
  inherit: {
    $$css: true
  }
};
export const variantToElement = {
  'hero-1': 'div',
  'hero-2': 'div',
  'hero-3': 'div',
  'hero-4': 'div',
  'heading-1': 'h1',
  'heading-2': 'h2',
  'heading-3': 'h3',
  'heading-4': 'h4',
  'heading-5': 'h5',
  'heading-6': 'h6',
  'body-sm': 'p',
  'body-md': 'p',
  'body-lg': 'p',
  strong: 'strong',
  code: 'code',
  inherit: 'span'
};

/**
 * Internal component that handles truncation with overflow-tooltip logic.
 * Isolated so that non-truncated Text instances pay zero hook cost.
 */
const TextWithTruncation = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(48);
  let as;
  let children;
  let layoutId;
  let lineClamp;
  let restProps;
  let t1;
  let t2;
  let tooltipHandle;
  let xstyle;
  if ($[0] !== t0) {
    const {
        as: t3,
        children: t4,
        truncated: t5,
        lineClamp: t6,
        tooltipHandle: t7,
        layoutId: t8,
        variant: t9,
        xstyle: t10
      } = t0,
      t11 = _objectWithoutPropertiesLoose(t0, _excluded);
    as = t3;
    children = t4;
    t1 = t5;
    lineClamp = t6;
    tooltipHandle = t7;
    layoutId = t8;
    t2 = t9;
    xstyle = t10;
    restProps = t11;
    $[0] = t0;
    $[1] = as;
    $[2] = children;
    $[3] = layoutId;
    $[4] = lineClamp;
    $[5] = restProps;
    $[6] = t1;
    $[7] = t2;
    $[8] = tooltipHandle;
    $[9] = xstyle;
  } else {
    as = $[1];
    children = $[2];
    layoutId = $[3];
    lineClamp = $[4];
    restProps = $[5];
    t1 = $[6];
    t2 = $[7];
    tooltipHandle = $[8];
    xstyle = $[9];
  }
  const truncated = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "inherit" : t2;
  let t3;
  if ($[10] !== layoutId) {
    t3 = {
      layoutId
    };
    $[10] = layoutId;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    layoutConstraintStyles
  } = useLayoutConstraints(t3);
  const EffectiveElement = as || variantToElement[variant];
  const isValidLineClamp = lineClamp !== undefined && lineClamp > 0;
  const isStringChildren = typeof children === "string";
  const internalRef = useRef(null);
  let t4;
  if ($[12] !== ref) {
    t4 = mergeRefs(ref, internalRef);
    $[12] = ref;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const mergedRef = t4;
  const [tooltipOpen, setTooltipOpen] = useState(false);
  let t5;
  if ($[14] !== isValidLineClamp) {
    t5 = open => {
      if (!open) {
        setTooltipOpen(false);
        return;
      }
      const el = internalRef.current;
      if (!el) {
        return;
      }
      const overflows = isValidLineClamp ? el.scrollHeight > el.clientHeight : el.scrollWidth > el.clientWidth;
      setTooltipOpen(overflows);
    };
    $[14] = isValidLineClamp;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleTooltipOpenChange = t5;
  const t6 = mergedRef;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      [DATA_COMPONENT_ATTRIBUTE]: COMPONENT_NAME
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== EffectiveElement || $[18] !== isValidLineClamp || $[19] !== layoutConstraintStyles || $[20] !== lineClamp || $[21] !== truncated || $[22] !== variant || $[23] !== xstyle) {
    t8 = stylex.props(resetStyles.base, truncated && truncateStyles.base, truncated && INLINE_AS_ELEMENTS.has(EffectiveElement) && truncateStyles.inlineBlock, isValidLineClamp && truncateStyles.lineClamp(lineClamp), variantStyles[variant], layoutConstraintStyles, xstyle);
    $[17] = EffectiveElement;
    $[18] = isValidLineClamp;
    $[19] = layoutConstraintStyles;
    $[20] = lineClamp;
    $[21] = truncated;
    $[22] = variant;
    $[23] = xstyle;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== EffectiveElement || $[26] !== children || $[27] !== restProps || $[28] !== t6 || $[29] !== t8) {
    t9 = /*#__PURE__*/_jsx(EffectiveElement, Object.assign({}, restProps, {
      ref: t6
    }, t7, t8, {
      children: children
    }));
    $[25] = EffectiveElement;
    $[26] = children;
    $[27] = restProps;
    $[28] = t6;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const element = t9;
  if (isStringChildren) {
    if (tooltipHandle) {
      let t10;
      if ($[31] !== children) {
        t10 = /*#__PURE__*/_jsx(TooltipContent, {
          children: children
        });
        $[31] = children;
        $[32] = t10;
      } else {
        t10 = $[32];
      }
      let t11;
      if ($[33] !== element || $[34] !== handleTooltipOpenChange || $[35] !== t10 || $[36] !== tooltipHandle || $[37] !== tooltipOpen) {
        t11 = /*#__PURE__*/_jsxs(TooltipRoot, {
          handle: tooltipHandle,
          open: tooltipOpen,
          onOpenChange: handleTooltipOpenChange,
          children: [element, t10]
        });
        $[33] = element;
        $[34] = handleTooltipOpenChange;
        $[35] = t10;
        $[36] = tooltipHandle;
        $[37] = tooltipOpen;
        $[38] = t11;
      } else {
        t11 = $[38];
      }
      return t11;
    }
    let t10;
    if ($[39] !== element) {
      t10 = /*#__PURE__*/_jsx(TooltipTrigger, {
        render: element
      });
      $[39] = element;
      $[40] = t10;
    } else {
      t10 = $[40];
    }
    let t11;
    if ($[41] !== children) {
      t11 = /*#__PURE__*/_jsx(TooltipContent, {
        children: children
      });
      $[41] = children;
      $[42] = t11;
    } else {
      t11 = $[42];
    }
    let t12;
    if ($[43] !== handleTooltipOpenChange || $[44] !== t10 || $[45] !== t11 || $[46] !== tooltipOpen) {
      t12 = /*#__PURE__*/_jsxs(TooltipRoot, {
        open: tooltipOpen,
        onOpenChange: handleTooltipOpenChange,
        children: [t10, t11]
      });
      $[43] = handleTooltipOpenChange;
      $[44] = t10;
      $[45] = t11;
      $[46] = tooltipOpen;
      $[47] = t12;
    } else {
      t12 = $[47];
    }
    return t12;
  }
  return element;
});
TextWithTruncation.displayName = 'TextWithTruncation';

/**
 * Low-level primitive for rendering text with consistent typography and optional truncation. Prefer HeroText, HeadingText, or BodyText for standard product copy; reach for Text when you need inline variants or custom typography.
 *
 * This component is part of Trellis's compositive API layer. Prefer the config
 * API, which covers the majority of use cases. Only reach for this component
 * when the config API can't express your pattern, and share your use case in
 * #trellis-support — it directly informs how we evolve the config layer.
 *
 * @see {@link __TextConfig | Text}
 */
export const Text = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(34);
  let as;
  let children;
  let layoutId;
  let lineClamp;
  let restProps;
  let t1;
  let t2;
  let tooltipHandle;
  let xstyle;
  if ($[0] !== t0) {
    const {
        as: t3,
        children: t4,
        truncated: t5,
        lineClamp: t6,
        tooltipHandle: t7,
        layoutId: t8,
        variant: t9,
        xstyle: t10
      } = t0,
      t11 = _objectWithoutPropertiesLoose(t0, _excluded2);
    as = t3;
    children = t4;
    t1 = t5;
    lineClamp = t6;
    tooltipHandle = t7;
    layoutId = t8;
    t2 = t9;
    xstyle = t10;
    restProps = t11;
    $[0] = t0;
    $[1] = as;
    $[2] = children;
    $[3] = layoutId;
    $[4] = lineClamp;
    $[5] = restProps;
    $[6] = t1;
    $[7] = t2;
    $[8] = tooltipHandle;
    $[9] = xstyle;
  } else {
    as = $[1];
    children = $[2];
    layoutId = $[3];
    lineClamp = $[4];
    restProps = $[5];
    t1 = $[6];
    t2 = $[7];
    tooltipHandle = $[8];
    xstyle = $[9];
  }
  const truncated = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "inherit" : t2;
  let t3;
  if ($[10] !== layoutId) {
    t3 = {
      layoutId
    };
    $[10] = layoutId;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    layoutConstraintStyles
  } = useLayoutConstraints(t3);
  const isValidLineClamp = lineClamp !== undefined && lineClamp > 0;
  const hasTruncation = truncated || isValidLineClamp;
  if (hasTruncation) {
    let t4;
    if ($[12] !== as || $[13] !== children || $[14] !== layoutId || $[15] !== lineClamp || $[16] !== ref || $[17] !== restProps || $[18] !== tooltipHandle || $[19] !== truncated || $[20] !== variant || $[21] !== xstyle) {
      t4 = /*#__PURE__*/_jsx(TextWithTruncation, Object.assign({}, restProps, {
        ref: ref,
        as: as,
        layoutId: layoutId,
        truncated: truncated,
        lineClamp: lineClamp,
        tooltipHandle: tooltipHandle,
        variant: variant,
        xstyle: xstyle,
        children: children
      }));
      $[12] = as;
      $[13] = children;
      $[14] = layoutId;
      $[15] = lineClamp;
      $[16] = ref;
      $[17] = restProps;
      $[18] = tooltipHandle;
      $[19] = truncated;
      $[20] = variant;
      $[21] = xstyle;
      $[22] = t4;
    } else {
      t4 = $[22];
    }
    return t4;
  }
  const EffectiveElement = as || variantToElement[variant];
  const t4 = ref;
  let t5;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      [DATA_COMPONENT_ATTRIBUTE]: COMPONENT_NAME
    };
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== layoutConstraintStyles || $[25] !== variant || $[26] !== xstyle) {
    t6 = stylex.props(resetStyles.base, variantStyles[variant], layoutConstraintStyles, xstyle);
    $[24] = layoutConstraintStyles;
    $[25] = variant;
    $[26] = xstyle;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== EffectiveElement || $[29] !== children || $[30] !== restProps || $[31] !== t4 || $[32] !== t6) {
    t7 = /*#__PURE__*/_jsx(EffectiveElement, Object.assign({}, restProps, {
      ref: t4
    }, t5, t6, {
      children: children
    }));
    $[28] = EffectiveElement;
    $[29] = children;
    $[30] = restProps;
    $[31] = t4;
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  return t7;
});
Text.displayName = COMPONENT_NAME;